// Insights.jsx
import React, { useMemo } from "react";
import styles from './insight.module.css'

const SLICE_COLORS = {
    completed: '#1baf7a',
    cancelled: '#e34948',
    pending: '#c9a227',
    unresolved: '#9aa0a6', // rides that are neither completed nor cancelled (e.g. in-progress)
};

// Builds SVG arc path for a single pie slice given a running angle offset (degrees).
const describeArc = (cx, cy, r, startAngle, endAngle) => {
    const toRad = (deg) => (deg - 90) * (Math.PI / 180);
    const start = { x: cx + r * Math.cos(toRad(startAngle)), y: cy + r * Math.sin(toRad(startAngle)) };
    const end = { x: cx + r * Math.cos(toRad(endAngle)), y: cy + r * Math.sin(toRad(endAngle)) };
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${cx},${cy} L ${start.x},${start.y} A ${r},${r} 0 ${largeArc} 1 ${end.x},${end.y} Z`;
};

const Insights = ({ data = [] }) => {

    console.log(data, 'here in insight')
    const totals = useMemo(() => {
        return data.reduce((acc, item) => {
            acc.rides += item.rides || 0;
            acc.completed += item.completed || 0;
            acc.cancelled += item.cancelled || 0;
            acc.requests += item.requests || 0;
            acc.pending += item.pending || 0;
            return acc;
        }, { rides: 0, completed: 0, cancelled: 0, requests: 0, pending: 0 });
    }, [data]);

    const unresolved = Math.max(totals.rides - totals.completed - totals.cancelled, 0);

    const slices = useMemo(() => {
        const parts = [
            { key: 'completed', value: totals.completed },
            { key: 'cancelled', value: totals.cancelled },
            { key: 'unresolved', value: unresolved },
        ].filter(p => p.value > 0);

        const total = parts.reduce((sum, p) => sum + p.value, 0) || 1;

        // Single-slice edge case: a 0→360 arc collapses to nothing because
        // start and end points are identical. Draw a full circle instead.
        if (parts.length === 1) {
            return [{
                ...parts[0],
                path: `M 50,5 A 45,45 0 1 1 49.99,5 Z`,
                percent: 100,
            }];
        }

        let angle = 0;
        return parts.map(p => {
            const sliceAngle = (p.value / total) * 360;
            const path = describeArc(50, 50, 45, angle, angle + sliceAngle);
            angle += sliceAngle;
            return { ...p, path, percent: Math.round((p.value / total) * 100) };
        });
    }, [totals, unresolved]);

    const completionRate = totals.rides > 0 ? Math.round((totals.completed / totals.rides) * 100) : 0;
    const cancellationRate = totals.rides > 0 ? Math.round((totals.cancelled / totals.rides) * 100) : 0;
    const pendingRate = totals.requests > 0 ? Math.round((totals.pending / totals.requests) * 100) : 0;
    const fulfillmentRate = totals.requests > 0 ? Math.round((totals.rides / totals.requests) * 100) : 0;

    if (data.length === 0 || totals.rides === 0) {
        return (
            <div className={styles.contain}>
                <p className={styles.emptyState}>No data for this period.</p>
            </div>
        );
    }

    return (
        <div className={styles.contain}>
            <div className={styles.pieRow}>
                <svg viewBox="0 0 100 100" className={styles.pie}>
                    {slices.map(slice => (
                        <path key={slice.key} d={slice.path} fill={SLICE_COLORS[slice.key]} />
                    ))}
                    <circle cx="50" cy="50" r="26" className={styles.pieHole} />
                    <text x="50" y="47" textAnchor="middle" className={styles.pieCenterValue}>{totals.rides}</text>
                    <text x="50" y="60" textAnchor="middle" className={styles.pieCenterLabel}>rides</text>
                </svg>

                <div className={styles.legend}>
                    {slices.map(slice => (
                        <div key={slice.key} className={styles.legendRow}>
                            <span className={styles.legendSwatch} style={{ background: SLICE_COLORS[slice.key] }} />
                            <span className={styles.legendLabel}>{slice.key}</span>
                            <span className={styles.legendValue}>{slice.value} ({slice.percent}%)</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* <div className={styles.statGrid}>
                <StatCard label="Completion rate" value={`${completionRate}%`} accent="#1baf7a" />
                <StatCard label="Cancellation rate" value={`${cancellationRate}%`} accent="#e34948" />
                <StatCard label="Requests fulfilled" value={`${fulfillmentRate}%`} accent="#2c2c8f" sub={`${totals.rides} of ${totals.requests}`} />
                <StatCard label="Pending requests" value={`${pendingRate}%`} accent="#c9a227" sub={`${totals.pending} of ${totals.requests}`} />
            </div> */}
        </div>
    );
};

const StatCard = ({ label, value, accent, sub }) => (
    <div className={styles.statCard} style={{ borderTopColor: accent }}>
        <div className={styles.statValue}>{value}</div>
        <div className={styles.statLabel}>{label}</div>
        {sub && <div className={styles.statSub}>{sub}</div>}
    </div>
);

export default Insights;